import { TheNightlySection } from '@news-mono/common'
import {
    breakpoint,
    calcRem,
    metrics,
    sectionThemeOverrider,
    theNightlyTextOnlySectionHeaderOverride,
} from '@news-mono/component-library'
import {
    getSocialImageMeta,
    PageType,
    TogglesReduxState,
    GetRouteAdTargeting,
    isElectionFeatureEnabled,
    getElectionAdTargeting,
    ElectionDefinition,
} from '@news-mono/web-common'
import { layout } from '../../../App.routing'
import { parsePageParam } from '../../helpers'
import queryString from 'query-string'
import { getYear } from 'date-fns'
import { wrapWithElectionContext } from '../routes'

type CreateElectionPageArgs = {
    getAdTargeting: GetRouteAdTargeting
    electionDefinition: ElectionDefinition
    toggles: TogglesReduxState
}

export const createElectionMapPage = ({
    getAdTargeting,
    electionDefinition,
    toggles,
}: CreateElectionPageArgs): PageType<TheNightlySection> => {
    const isHeadToHeadEnabled = isElectionFeatureEnabled(
        toggles,
        electionDefinition.electionId,
        'election-section-head-to-head',
    )

    const isHeadToHeadCountdownEnabled = isElectionFeatureEnabled(
        toggles,
        electionDefinition.electionId,
        'election-head-to-head-countdown',
    )

    const isFederal =
        electionDefinition.electionData?.config?.electionType.toLowerCase() ===
        'federal'
    const electionYear = getYear(
        new Date(electionDefinition.electionData?.config?.electionDate ?? ''),
    )
    const electionState = electionDefinition.electionData?.config?.state

    const meta = electionDefinition.meta

    let defaultTitle

    if (isFederal) {
        defaultTitle = ` Map of Australian Federal Election ${electionYear}`
    } else {
        defaultTitle = `Map of Australian ${electionState} Election ${electionYear}`
    }

    const seoTitle = meta.map?.seoTitle ?? defaultTitle
    const seoDescription = meta.map?.seoDescription

    const section = 'politics'

    return {
        kind: 'page',
        heading: seoTitle,
        pageType: 'static',
        section: section,
        hideHeading: true,
        adTargeting: getElectionAdTargeting(
            electionDefinition,
            getAdTargeting,
            'map-page',
        ),
        pageMeta: {
            title: seoTitle,
            description: seoDescription,
            link: meta.canonical
                ? [
                      {
                          rel: 'canonical',
                          href: `${meta.canonical}politics/${electionDefinition.electionId}/map`,
                      },
                  ]
                : undefined,
            meta: meta.updates?.socialImage
                ? getSocialImageMeta(meta.updates.socialImage, '2400', '1260')
                : undefined,
        },
        socialMeta: {
            title: seoTitle,
            description: seoDescription,
        },
        additionalPageProperties: {
            hasTopGutter: false,
        },

        compositions: wrapWithElectionContext([
            layout.nestedComposition({
                type: 'scheme-overrider',
                props: {
                    override: sectionThemeOverrider('thenightly', section),
                },
                contentAreas: {
                    children: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                containerWidth:
                                    metrics.thenightly.siteMetrics
                                        .mainContentWidth,
                                fullWidth: true,
                                flex: {
                                    default: {
                                        flexDirection: 'column',
                                        gap: calcRem(48),
                                        paddingBottom: calcRem(80),
                                        [breakpoint('sm')]: {
                                            gap: calcRem(80),
                                            paddingBottom: 0,
                                        },
                                    },
                                    sm: {},
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            verticalSpacing: 'unset',
                                            breakpoints: {
                                                lg: {
                                                    verticalSpacing: 'unset',
                                                },
                                            },
                                        },
                                        contentAreas: {
                                            main: [
                                                ...(isHeadToHeadEnabled
                                                    ? [
                                                          layout.component({
                                                              type: 'election-head-to-head-seat-count-widget',
                                                              props: {
                                                                  electionDefinition,
                                                                  countdownEnabled:
                                                                      isHeadToHeadCountdownEnabled,
                                                              },
                                                          }),
                                                      ]
                                                    : []),
                                                layout.component({
                                                    type: 'election-navigation',
                                                    props: {
                                                        electionDefinition,
                                                        activePage: 'map',
                                                        headingLevel: 'h2',
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            horizontalSpacing: 'outerMargin',
                                            horizontalSpacingBreakpoint: 'lg',
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.nestedComposition({
                                                    type: 'component-overrider',
                                                    props: {
                                                        override: () =>
                                                            theNightlyTextOnlySectionHeaderOverride,
                                                    },
                                                    contentAreas: {
                                                        children: [
                                                            layout.component({
                                                                type: 'section-header',
                                                                props: {
                                                                    margin: {
                                                                        default:
                                                                            {
                                                                                marginTop: 40,
                                                                                marginBottom: 24,
                                                                            },
                                                                        lg: {
                                                                            marginTop: 32,
                                                                        },
                                                                    },
                                                                    heading:
                                                                        'Election Map',
                                                                    isStandalonePage:
                                                                        true,
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                                layout.component({
                                                    type: 'election-map-overview-widget',
                                                    props: {
                                                        headingLevel: 'h1',
                                                        titleText: '',
                                                        electionDefinition,
                                                        hasShare: true,
                                                    },
                                                }),
                                                layout.component({
                                                    type: 'ad-unit',
                                                    props: {
                                                        adType: 'inline',
                                                        noticePosition:
                                                            'above-center',
                                                        slot: {
                                                            id: '0-election-1',
                                                            size: 'leaderboardAbove768MrecBelow',
                                                        },
                                                        padding: [0, 0, 0, 0],
                                                        lazyLoadingDistance: 0,
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ]),
    }
}
